import { memo } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { AXIS_TICK, CHART_RESIZE_DEBOUNCE_MS, HOVER_CURSOR } from '../../utils/chartFormatting';
import { FLOW_HEX, type FlowPoint, type LabeledPoint, type TrendPoint } from './teamStatsModel';

// Recharts wrappers for the admin home, styled like the billing overview's
// (components/billing/overview/OverviewCharts.tsx) so the dashboards read as one system.

// Every Home chart labels its x-axis with the same few bucket dates.
const DATE_AXIS_PROPS = {
    dataKey: 'label',
    tick: AXIS_TICK,
    axisLine: false,
    tickLine: false,
    tickMargin: 6,
    interval: 'preserveStartEnd',
    minTickGap: 16
} as const;
// Room for one row of 11px tick labels under a sparkline.
const SPARK_AXIS_HEIGHT_PX = 20;
const SPARK_MARGIN = { top: 4, right: 2, left: 2, bottom: 0 } as const;

const TREND_PLOT_HEIGHT_PX = 56;
const TREND_HEIGHT_PX = TREND_PLOT_HEIGHT_PX + SPARK_AXIS_HEIGHT_PX;
const TREND_FILL_OPACITY = 0.12;

const KPI_PLOT_HEIGHT_PX = 48;
export const KPI_TREND_HEIGHT_PX = KPI_PLOT_HEIGHT_PX + SPARK_AXIS_HEIGHT_PX;
const KPI_MAX_BAR_PX = 16;
const KPI_STACK_ID = 'kpi';
const KPI_TOP_RADIUS: [number, number, number, number] = [2, 2, 0, 0];

const FLOW_HEIGHT_PX = 96;
const FLOW_MARGIN = { top: 4, right: 4, left: -16, bottom: 0 } as const;
const FLOW_MAX_BAR_PX = 40;
const FLOW_Y_TICK_COUNT = 3;

interface TooltipEntry {
    name?: string | number;
    value?: number | string;
    color?: string;
}

interface ChartTooltipProps {
    active?: boolean;
    label?: string | number;
    payload?: TooltipEntry[];
}

function ChartTooltip({ active, label, payload }: ChartTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3.5 py-2.5 min-w-[150px]">
            {label !== undefined && (
                <p className="text-xs font-bold text-gray-800 mb-1.5">{label}</p>
            )}
            <div className="flex flex-col gap-1">
                {payload.map((entry) => (
                    <div key={String(entry.name)} className="flex items-center gap-2">
                        <span
                            className="w-2 h-2 rounded-[2px] shrink-0"
                            style={{ background: entry.color ?? '#9ca3af' }}
                        />
                        <span className="text-[11px] text-gray-500 flex-1">{entry.name}</span>
                        <span className="text-[11px] font-semibold text-gray-900 tabular-nums">
                            {entry.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface TeamTrendChartProps {
    points: TrendPoint[];
    color: string;
    height?: number;
}

// A sparkline: no visible axes, and a y-domain fitted to the data so a small
// swing on a big queue still shows as movement.
export const TeamTrendChart = memo(function TeamTrendChart({
    points,
    color,
    height = TREND_HEIGHT_PX
}: TeamTrendChartProps) {
    return (
        <ResponsiveContainer
            width="100%"
            height={height}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <AreaChart data={points} margin={SPARK_MARGIN}>
                <XAxis {...DATE_AXIS_PROPS} height={SPARK_AXIS_HEIGHT_PX} />
                <YAxis hide allowDecimals={false} domain={['dataMin', 'dataMax']} />
                <Tooltip content={<ChartTooltip />} isAnimationActive={false} />
                <Area
                    type="monotone"
                    dataKey="open"
                    name="Open"
                    stroke={color}
                    strokeWidth={2}
                    fill={color}
                    fillOpacity={TREND_FILL_OPACITY}
                    dot={false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={false}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
});

export interface KpiSeries {
    dataKey: string;
    name: string;
    color: string;
}

interface KpiTrendChartProps {
    points: readonly LabeledPoint[];
    /** Stacked bottom to top; one entry draws a plain bar series. */
    bars: readonly KpiSeries[];
}

export const KpiTrendChart = memo(function KpiTrendChart({ points, bars }: KpiTrendChartProps) {
    return (
        <ResponsiveContainer
            width="100%"
            height={KPI_TREND_HEIGHT_PX}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <BarChart data={points} margin={SPARK_MARGIN} barCategoryGap="16%">
                <XAxis {...DATE_AXIS_PROPS} height={SPARK_AXIS_HEIGHT_PX} />
                <YAxis hide allowDecimals={false} />
                <Tooltip
                    cursor={HOVER_CURSOR}
                    content={<ChartTooltip />}
                    isAnimationActive={false}
                />
                {bars.map((bar, index) => (
                    <Bar
                        key={bar.dataKey}
                        dataKey={bar.dataKey}
                        name={bar.name}
                        stackId={KPI_STACK_ID}
                        fill={bar.color}
                        radius={index === bars.length - 1 ? KPI_TOP_RADIUS : undefined}
                        maxBarSize={KPI_MAX_BAR_PX}
                        isAnimationActive={false}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
});

// Same build as the monitoring overview's ActivityChart, shrunk to card size.
export const TeamFlowChart = memo(function TeamFlowChart({ points }: { points: FlowPoint[] }) {
    return (
        <ResponsiveContainer
            width="100%"
            height={FLOW_HEIGHT_PX}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <ComposedChart data={points} margin={FLOW_MARGIN} barCategoryGap="24%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis {...DATE_AXIS_PROPS} />
                <YAxis
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    tickCount={FLOW_Y_TICK_COUNT}
                />
                <Tooltip
                    cursor={HOVER_CURSOR}
                    content={<ChartTooltip />}
                    isAnimationActive={false}
                />
                <Bar
                    dataKey="inflow"
                    name="In"
                    fill={FLOW_HEX.inflow}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={FLOW_MAX_BAR_PX}
                    isAnimationActive={false}
                />
                <Line
                    type="monotone"
                    dataKey="throughput"
                    name="Treated"
                    stroke={FLOW_HEX.throughput}
                    strokeWidth={2}
                    dot={{ r: 2.5, strokeWidth: 0, fill: FLOW_HEX.throughput }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
});
